import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';

import { IFormImperativeHandleProps } from '../../../../../../common/form';
import { IStepComponentProps, IStepperImperativeHandleProps } from '../../../../../../common/stepper';
import { yup } from '../../../../../../../services/yup';
import { IStepsState } from '../../vision-profile.models';
import { UPDATE_VISION_PROFILE } from '../../../../../../../graphql/profile/mutations';
import { GET_VISION_PROFILE } from '../../../../../../../graphql/profile/queries/get-vision-profile';
import { useCancellablePromise } from '../../../../../../../hooks/use-cancellable-promise/use-cancellable-promise';
import { useActionsInProgress } from '../../../../../../../graphql/preloader/actions/actions-in-progress';
import { IVisionProfileData } from '../../../../../../../graphql/profile/models/get-vision-profile.models';
import { promiseErrorCallbacks } from '../../../../../../../utils/promise/set-promise-error-callbacks';
import { convertDateToString } from '../../../../../../../utils/date/convert-date-to-string';
import { LastVisionCheckView } from '../../../vision-profile-views/last-vision-check-view';
import { TFormValues } from '../../../vision-profile-views/last-vision-check-view/last-vision-check-form';

export const LastVisionCheckStep = forwardRef<
IStepperImperativeHandleProps, IStepComponentProps>(({
  onGoNextSuccess,
  stepsState,
  onNextButtonDisabled,
}, ref): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const intl = useIntl();
  const { lastVisionCheck: lastVisionCheckState } = stepsState as IStepsState;
  const { data, updateQuery } = useQuery<IVisionProfileData>(GET_VISION_PROFILE);
  const [updateVisionProfile] = useMutation(UPDATE_VISION_PROFILE);
  const { makeCancellablePromise, CancelledPromiseOnUnmountError } = useCancellablePromise();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const dateOfBirth = useMemo(
    () => data?.visionProfile?.dateOfBirth as string,
    [data?.visionProfile?.dateOfBirth],
  );

  const defaultValues = useMemo(() => lastVisionCheckState || {
    lastVisionCheck: null,
  }, []);

  const validationSchema = useMemo(() => yup
    .object({
      lastVisionCheck: yup
        .date()
        .nullable()
        .required(intl.formatMessage({
          id: 'vp.lastVisionCheck.dateInput.errors.required' })),
    }),
  [intl],
  );

  const handleUpdateLastVisionCheck = async ({
    lastVisionCheck: lastVisionCheckCurrent,
  }: TFormValues) => {
    const lastVisionCheckString = convertDateToString(lastVisionCheckState?.lastVisionCheck);
    const lastVisionCheckCurrentString = convertDateToString(lastVisionCheckCurrent)!.slice(0, 7);

    if (lastVisionCheckCurrentString === lastVisionCheckString) {
      onGoNextSuccess({ lastVisionCheck: lastVisionCheckString });
      return;
    }

    addActionInProgress();

    try {
      const { data: { patchVisionProfile } } = await makeCancellablePromise(
        updateVisionProfile({
          variables: {
            visionProfile: {
              lastVisionCheck: lastVisionCheckCurrentString,
            },
          },
        }),
      );

      updateQuery((prevState) => ({
        visionProfile: {
          ...prevState.visionProfile,
          ...patchVisionProfile,
        },
      }));

      removeActionInProgress();
      onGoNextSuccess({ lastVisionCheck: patchVisionProfile.lastVisionCheck });
    } catch (error) {
      if (error instanceof CancelledPromiseOnUnmountError) {
        return;
      }

      if (promiseErrorCallbacks.anyError) {
        promiseErrorCallbacks.anyError();
      }

      removeActionInProgress();
    }
  };

  const handleValidateDependencies = (isValid: boolean) => {
    onNextButtonDisabled(!isValid);
  };

  useImperativeHandle(ref, () => ({
    goNext() {
      formRef.current!.submit();
    },
    goBack() {
      return formRef.current!.getValues();
    },
  }));

  return (
    <LastVisionCheckView
      ref={formRef}
      dateOfBirth={dateOfBirth}
      formProps={{
        onSubmit: handleUpdateLastVisionCheck,
        onValidateDependencies: handleValidateDependencies,
        defaultValues,
        validationSchema,
        validationMode: 'onChange',
      }}
    />
  );
});
